import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input
} from '@invent/wl-ui-kit-next/ui';
import { ArrowLeft, BarChart3, ChevronRight, History, Plus, Send } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ChoiceSelect, conflictWords, HelpPopover, NotPermitted, StatusChip } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { isCustomGroup, type IMailPlaceholderCatalogue } from '../../api/mail-template-contracts';
import type {
  IMailBody,
  IMailDraft,
  IMailSettings,
  IMailTemplate,
  IMailTemplateSummary,
  IMailVariant,
  IMailVersion,
  MailPart,
  MailSurvey
} from '../../api/mail-template-contracts';
import { AdminPage, LoadError, TableSkeleton, useAdminData, useDebounced } from '../../components/admin-page';
import { fieldRefusal, refusalMessage } from '../../refusal';
import {
  BLOCK_PICK,
  hasPreviewProblems,
  PREVIEW_DEFAULTS,
  PreviewBody,
  PreviewToolbar,
  renderOptions,
  usePreview,
  type IPreviewOptions
} from './mail-preview';
import { EditorLookProvider, lookOf, useDesigns } from './design-look';
import { PlaceholderField, RichField } from './placeholder-field';
import {
  placeholderSections,
  placeholdersInSheetOrder,
  readPlaceholderCatalogue,
  type PlaceholderSections
} from './placeholder-catalogue';
import { PlaceholderSheet, type IPlaceholderSheetHandle } from './placeholder-sheet';
import { CommandsButton } from './editor-commands';
import {
  isOwnPlaceholder,
  type IPlaceholderFieldHandle
} from './placeholder-text';
import { PublishDialog, type IPublishChoice } from './publish-dialog';
import { useDraftSaving, type DraftAnswer } from './draft-saving';
import {
  bodyOf,
  changedKeys,
  formatEt,
  isInvalidBody,
  isRich,
  lineText,
  problemsOf,
  qualityHints,
  sameBody,
  settingProblems,
  sortProblems,
  surveyLabel,
  SURVEYS,
  versionStamp,
  type FieldProblems
} from './template-fields';
import { canEditTemplate, TemplateFormDialog, TemplateMenu } from './template-admin';
import { DeliverySheet } from './template-delivery';
import { HISTORY_ROOM, HistorySheet, VersionView } from './template-history';
import { hasStatus, STATUS_PARAM, statusOf, STATUSES, useEveryTemplate, useTemplateTabs, type TemplateStatus } from './template-nav';
import { TemplateOverview } from './template-overview';
import { TemplatePicker } from './template-picker';
import { HeaderTags, UsageChips } from './template-tags';

/**
 * Setup → Email templates (SR-523): every email the portal sends, its editable parts — a subject,
 * a preview text and one block body — variant by variant. Opened without `?template=` the page lists the templates
 * (`TemplateOverview`); a row opens one, and the header's crumb leads back. In a template, a header
 * names the email (a searchable list, grouped) and, for a survey's email, the survey whose text is
 * edited; the draft saves itself, the Lists way (`draft-saving.tsx`), and Publish… makes it a
 * version, now or from a date. Under it the parts beside a sticky preview survey-api renders
 * exactly as the worker sends it, linked both ways. History opens at the right. Every route is
 * `mail.templates`; survey-api judges every part, placeholder and condition.
 *
 * Staff create their own templates here too (SR-523 stages 3 and 5): New template, its details —
 * where it can be used (Lists `mail.outreach.author`, reminders `mail.templates`) and its tags —
 * Archive and Restore. Every template's placeholders are read per variant (`GET …/placeholders`):
 * its own, then — where it knows a participant and a survey — the field catalogue of the variant's
 * survey, all in one sheet (`placeholder-sheet.tsx`); every command and shortcut is listed behind
 * the keyboard button by Content (`editor-commands.tsx`). Every
 * template's body is one block document; a built-in one's holds the portal's own blocks, locked. A
 * tag and a status — Active, Archived or All — narrow the list.
 */

const TEMPLATE_PARAM = 'template';
const SURVEY_PARAM = 'survey';
const TAG_PARAM = 'tag';

export function MailTemplatesPage() {
  const can = useCan();
  if (!can('mail.templates') && !can('mail.outreach.author')) {
    return (
      <AdminPage title="Email templates">
        <NotPermitted what="editing the emails the portal sends" />
      </AdminPage>
    );
  }
  return <MailTemplates />;
}

function MailTemplates() {
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const status = statusOf(params.get(STATUS_PARAM));
  // survey-api lists the archived instead of the live: the Status filter's counts and the
  // switcher's Archived group read both.
  const list = useEveryTemplate();
  // Only what the caller may edit: an outreach author without `mail.templates` sees the custom
  // templates used from Lists alone.
  const templates = (list.data ?? []).filter((template) => canEditTemplate(can, template));
  const selected = params.get(TEMPLATE_PARAM);
  // One this seat may not edit is not opened — survey-api would refuse its read — but said so.
  const editable = can('mail.templates') || templates.some((template) => template.code === selected);
  const asked = params.get(SURVEY_PARAM);
  const survey = SURVEYS.find((entry) => entry.value === asked)?.value ?? null;
  const tag = params.get(TAG_PARAM) ?? '';
  const tagged = tag ? templates.filter((template) => hasTag(template.tags, tag)) : templates;
  const shown = tagged.filter((template) => hasStatus(template, status));
  const creatable = can('mail.outreach.author') || can('mail.templates');
  const [creating, setCreating] = React.useState(false);
  const tabs = useTemplateTabs();

  /** A template, or its survey's variant; one opened from the list is a step Back returns from. */
  const go = (code: string, next: Nullable<MailSurvey>, replace = true) => {
    const query = new URLSearchParams({ [TEMPLATE_PARAM]: code });
    if (next) query.set(SURVEY_PARAM, next);
    if (status !== 'active') query.set(STATUS_PARAM, status);
    if (tag) query.set(TAG_PARAM, tag);
    setParams(query, { replace });
  };
  const chooseFilter = (param: string, next: string) => {
    const query = new URLSearchParams(params);
    if (next) query.set(param, next);
    else query.delete(param);
    setParams(query, { replace: true });
  };

  return (
    <AdminPage
      title="Email templates"
      crumb={selected && <OverviewLink status={status} tag={tag} />}
      tabs={selected ? undefined : tabs}
      actions={
        <span className="flex items-center gap-2">
          {!selected && <OverviewHelp />}
          {creatable && (
            <Button type="button" size="sm" onClick={() => setCreating(true)} data-new-template="">
              <Plus className="size-4" aria-hidden />
              New template
            </Button>
          )}
        </span>
      }
    >
      {!selected && (
        <TemplateOverview
          templates={list.data ? shown : null}
          error={list.error}
          tagged={Boolean(tag)}
          status={status}
          filters={
            <>
              <TagFilter
                templates={templates.filter((template) => hasStatus(template, status))}
                value={tag}
                onChange={(next) => chooseFilter(TAG_PARAM, next)}
              />
              <StatusFilter
                templates={tagged}
                value={status}
                onChange={(next) => chooseFilter(STATUS_PARAM, next === 'active' ? '' : next)}
              />
            </>
          }
          onOpen={(code) => go(code, null, false)}
        />
      )}
      {selected && list.error && <LoadError message={list.error} />}
      {selected && !list.data && !list.error && <TableSkeleton rows={3} />}
      {list.data && selected && !editable && <NotPermitted what="editing this email template" />}
      {list.data && selected && editable && (
        <TemplateEditor
          key={selected}
          code={selected}
          survey={survey}
          templates={templates}
          onSelect={(code) => go(code, null)}
          onSurvey={(next) => go(selected, next)}
          onListChanged={() => void list.reload()}
        />
      )}
      <TemplateFormDialog
        open={creating}
        onOpenChange={setCreating}
        // Opened once the list holds it: before, an outreach author's would read as not theirs.
        onDone={(created) => void list.reload().then(() => go(created.code, null, Boolean(selected)))}
      />
    </AdminPage>
  );
}

function hasTag(tags: readonly string[], tag: string): boolean {
  return tags.some((each) => each.toLowerCase() === tag.toLowerCase());
}

/** The tags on the templates listed, most used first; a choice narrows the list. */
function TagFilter({
  templates,
  value,
  onChange
}: {
  templates: readonly IMailTemplateSummary[];
  value: string;
  onChange: (tag: string) => void;
}) {
  const counts = new Map<string, { tag: string; count: number }>();
  for (const tag of templates.flatMap((template) => template.tags)) {
    const key = tag.toLowerCase();
    counts.set(key, { tag: counts.get(key)?.tag ?? tag, count: (counts.get(key)?.count ?? 0) + 1 });
  }
  const tags = [...counts.values()].sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
  if (tags.length === 0 && !value) return null;
  const known = tags.some((entry) => entry.tag.toLowerCase() === value.toLowerCase());
  return (
    <span className="w-48" data-tag-filter="">
      <ChoiceSelect
        aria-label="Tag"
        value={value}
        onChange={onChange}
        choices={[
          { value: '', label: 'All tags' },
          ...tags.map((entry) => ({ value: entry.tag, label: `${entry.tag} (${entry.count})` })),
          ...(value && !known ? [{ value, label: value }] : [])
        ]}
        className="h-8"
      />
    </span>
  );
}

/** Active, archived or all of the templates listed, each with its count; `active` by default. */
function StatusFilter({
  templates,
  value,
  onChange
}: {
  templates: readonly IMailTemplateSummary[];
  value: TemplateStatus;
  onChange: (status: TemplateStatus) => void;
}) {
  return (
    <span className="w-40" data-status-filter="">
      <ChoiceSelect
        aria-label="Status"
        value={value}
        onChange={(next) => onChange(statusOf(next))}
        choices={STATUSES.map(({ value: status, label }) => ({
          value: status,
          label: `${label} (${templates.filter((template) => hasStatus(template, status)).length})`
        }))}
        className="h-8"
      />
    </span>
  );
}

/** Back to the list of templates, filtered as it was. */
function OverviewLink({ status, tag }: { status: TemplateStatus; tag: string }) {
  const query = new URLSearchParams();
  if (status !== 'active') query.set(STATUS_PARAM, status);
  if (tag) query.set(TAG_PARAM, tag);
  const search = query.toString();
  return (
    <Link
      to={{ search: search ? `?${search}` : '' }}
      className="inline-flex items-center gap-1 text-sm text-[var(--color-sapphire)] underline-offset-4 hover:underline"
      data-templates-overview=""
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      All email templates
    </Link>
  );
}

function OverviewHelp() {
  return (
    <HelpPopover label="About email templates" align="end">
      <p>
        Every email the portal sends, and the emails staff write for Lists and for cycle reminders
        (Your templates). Open one to edit its text, see its history and send yourself a test.
        Tags only label templates; the tag list narrows the table to one.
      </p>
      <p>
        <em>Default</em>: the text written in the portal is sent. <em>Version N</em>: a published
        version is. <em>Not published</em>: a staff email with no version yet — nothing is sent with
        it. <em>Not in use</em>: the published version no longer passes the checks, so the default
        is sent. <em>Own text</em> names the surveys whose mails differ from the text for all surveys.
      </p>
      <p>Archived emails are listed under Status: Archived; no campaign or reminder can choose one.</p>
    </HelpPopover>
  );
}

/** The selected template, read; the editor remounted per variant and after every publication. */
function TemplateEditor({
  code,
  survey,
  templates,
  onSelect,
  onSurvey,
  onListChanged
}: {
  code: string;
  survey: Nullable<MailSurvey>;
  templates: IMailTemplateSummary[];
  onSelect: (code: string) => void;
  onSurvey: (survey: Nullable<MailSurvey>) => void;
  onListChanged: () => void;
}) {
  const read = useAdminData((signal) => mailTemplatesApi.get(code, signal), [code]);
  const [generation, setGeneration] = React.useState(0);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [options, setOptions] = React.useState<IPreviewOptions>(PREVIEW_DEFAULTS);
  const [renaming, setRenaming] = React.useState(false);
  const designs = useDesigns();
  const loaded = read.data;
  const staff = isCustomGroup(loaded?.group);
  const variantSurvey = loaded?.surveyScoped ? survey : null;
  // The variant's placeholders: the template's own, then the field catalogue of its survey — read
  // once the template says whether it has variants, so a survey's link reads its own alone.
  const catalogue = useAdminData<Nullable<IMailPlaceholderCatalogue>>(
    async (signal) => (loaded ? mailTemplatesApi.placeholders(code, variantSurvey, signal) : null),
    [code, variantSurvey, loaded?.surveyScoped]
  );

  // The Lists field catalogue those fields come from, which the picker groups them by — read
  // where the email knows a survey; the answer says which variant it is for.
  const listsKey = `${code}:${variantSurvey ?? 'base'}:${loaded?.surveyScoped ?? 'unknown'}`;
  const lists = useAdminData(
    async (signal) => ({
      key: listsKey,
      catalogue: loaded?.surveyScoped ? await readPlaceholderCatalogue(variantSurvey, signal) : null
    }),
    [listsKey]
  );
  const listsCatalogue = lists.data?.key === listsKey ? lists.data.catalogue : undefined;
  const sections = React.useMemo(
    () => (catalogue.data ? placeholderSections(catalogue.data.placeholders, listsCatalogue ?? null) : null),
    [catalogue.data, listsCatalogue]
  );

  if (read.error) return <LoadError message={read.error} />;
  if (!loaded || (catalogue.data === null && !catalogue.error) || listsCatalogue === undefined) {
    return <TableSkeleton rows={6} />;
  }
  const template = catalogue.data
    ? {
        ...loaded,
        placeholders: placeholdersInSheetOrder(catalogue.data.placeholders, sections ?? []),
        conditions: catalogue.data.conditions,
        lists: catalogue.data.lists
      }
    : loaded;
  const reload = async (message: Nullable<string>) => {
    setSaid(message);
    await read.reload();
    setGeneration((n) => n + 1);
    onListChanged();
  };
  // Saved at once, the editor kept as it stands: only the tags of the read change.
  const saveTags = async (tags: string[]): Promise<Nullable<string>> => {
    try {
      const saved = await mailTemplatesApi.patch(code, { tags });
      read.adopt((was) => was && { ...was, tags: saved.tags });
      onListChanged();
      return null;
    } catch (error) {
      return fieldRefusal(error, 'tags') ?? refusalMessage(error, 'The tags could not be saved.');
    }
  };

  return (
    <>
      {catalogue.error && (
        <LoadError message={`The placeholders could not be read: ${catalogue.error}`} />
      )}
      <Editor
        key={`${variantSurvey ?? 'base'}:${generation}`}
        template={template}
        sections={sections ?? placeholderSections(template.placeholders, null)}
        survey={variantSurvey}
        templates={templates}
        onSelect={onSelect}
        onSurvey={(next) => {
          setSaid(null);
          onSurvey(next);
        }}
        options={options}
        onOptions={setOptions}
        said={said}
        onAdopt={(change) => read.adopt((was) => was && change(was))}
        onReload={reload}
        onRefetch={async () => {
          await read.reload();
          onListChanged();
        }}
        onTags={saveTags}
        designs={designs}
        menu={
          staff
            ? (leave) => (
                <TemplateMenu
                  template={loaded}
                  busy={false}
                  leave={leave}
                  onRename={() => setRenaming(true)}
                  onArchived={(message) => void reload(message)}
                />
              )
            : undefined
        }
      />
      {staff && (
        <TemplateFormDialog
          open={renaming}
          onOpenChange={setRenaming}
          template={loaded}
          // Only the details change: the editor is kept as it stands, edits and all.
          onDone={(done) => {
            read.adopt((was) => was && { ...was, name: done.name, purpose: done.purpose, usages: done.usages });
            setSaid('Saved.');
            onListChanged();
          }}
        />
      )}
    </>
  );
}

/** A survey's variant has text of its own while a version of its own is current — survey-api
 *  answers the base's current for one that has none. */
function ownText(variant: Nullable<IMailVariant>): boolean {
  return Boolean(variant?.survey && variant.current && variant.current.survey === variant.survey);
}

/** The template with a variant's draft replaced — the variant added, for a survey's first draft. */
function withDraft(template: IMailTemplate, survey: Nullable<MailSurvey>, draft: IMailDraft): IMailTemplate {
  const exists = template.variants.some((variant) => variant.survey === survey);
  const variants = exists
    ? template.variants.map((variant) => (variant.survey === survey ? { ...variant, draft } : variant))
    : [...template.variants, { survey, current: null, scheduled: null, draft, versions: [] }];
  return { ...template, variants };
}

type Busy = Nullable<'draft' | 'publish' | 'withdraw' | 'remove' | 'discard'>;

function Editor({
  template,
  survey,
  templates,
  onSelect,
  onSurvey,
  options,
  onOptions,
  said,
  onAdopt,
  onReload,
  onRefetch,
  onTags,
  designs,
  menu,
  sections
}: {
  template: IMailTemplate;
  /** The placeholder picker's groups. */
  sections: PlaceholderSections;
  survey: Nullable<MailSurvey>;
  templates: IMailTemplateSummary[];
  onSelect: (code: string) => void;
  onSurvey: (survey: Nullable<MailSurvey>) => void;
  options: IPreviewOptions;
  onOptions: (options: IPreviewOptions) => void;
  said: Nullable<string>;
  /** A saved draft applied to the template as read now. */
  onAdopt: (change: (template: IMailTemplate) => IMailTemplate) => void;
  onReload: (message: Nullable<string>) => Promise<void>;
  /** The template read again, the editor kept as it stands — *Reload* after a conflict. */
  onRefetch: () => Promise<void>;
  /** The template's tags saved at once; null when saved, else why not. */
  onTags: (tags: string[]) => Promise<Nullable<string>>;
  designs: ReturnType<typeof useDesigns>;
  /** A staff template's Rename / Archive menu; what reads the editor afresh goes through `leave`. */
  menu?: (leave: (go: () => void) => void) => React.ReactNode;
}) {
  const code = template.code;
  const staff = isCustomGroup(template.group);
  const parts = template.parts;
  const keys = React.useMemo(() => parts.map(({ key }) => key), [parts]);
  const base = template.variants.find((variant) => variant.survey === null) ?? null;
  const variant = survey ? (template.variants.find((entry) => entry.survey === survey) ?? null) : base;
  const own = survey ? ownText(variant) : true;
  // What is sent now: the variant's current version, or for a survey without its own, the base's.
  const current: Nullable<IMailVersion> = own ? (variant?.current ?? null) : (base?.current ?? null);
  const draft = variant?.draft ?? null;
  const saved = React.useMemo(
    () => bodyOf(parts, draft ?? (own ? variant?.current : base?.current) ?? null),
    [parts, draft, own, variant, base]
  );
  const [body, setBody] = React.useState<IMailBody>(saved);
  const bodyRef = React.useRef(body);
  bodyRef.current = body;
  const [saveProblems, setSaveProblems] = React.useState<FieldProblems>({});
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [conflict, setConflict] = React.useState<Nullable<{ text: string; alert: boolean }>>(null);
  const [busy, setBusy] = React.useState<Busy>(null);
  const [loaded, setLoaded] = React.useState<Nullable<number>>(null);
  const [focus, setFocus] = React.useState<Nullable<string>>(null);
  // The part whose insert list is open: its value lacks the unfinished `{{…`, so no save.
  const [suggesting, setSuggesting] = React.useState<Nullable<string>>(null);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [deliveryOpen, setDeliveryOpen] = React.useState(false);
  const can = useCan();
  // The log's composite: an editor of templates or a sender of outreach, who reads responses.
  const canLog = (can('mail.templates') || can('mail.outreach.send')) && can('responses.browse');
  const [picked, setPicked] = React.useState<Nullable<number>>(current?.version ?? null);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [publishRefusal, setPublishRefusal] = React.useState<Nullable<string>>(null);
  const [senderOpen, setSenderOpen] = React.useState(false);
  const handles = React.useRef<Partial<Record<string, IPlaceholderFieldHandle>>>({});
  const sheet = React.useRef<IPlaceholderSheetHandle>(null);
  const lastFocused = React.useRef<string>(parts.find((part) => part.kind === 'rich')?.key ?? parts[0]?.key ?? '');
  const changed = !sameBody(parts, body, saved);
  const settled = useDebounced(body, 400);
  const preview = usePreview(code, survey, settled, keys, options);
  const [test, setTest] = React.useState<Nullable<{ sent: boolean; text: string }>>(null);
  const [testing, setTesting] = React.useState(false);
  const [edits, setEdits] = React.useState(0);
  // The draft as of now — its revision and its body — moved the moment a save lands, before the
  // screen draws it: a save started by a timer quotes the revision the last one left.
  const draftNow = React.useRef(draft);
  draftNow.current = draft;
  const savedNow = React.useRef(saved);
  savedNow.current = saved;

  // One stable registration per part, so a part's editor does not re-register on each render.
  const register = React.useMemo(
    () =>
      Object.fromEntries(
        keys.map((key) => [
          key,
          (handle: Nullable<IPlaceholderFieldHandle>) => {
            if (handle) handles.current[key] = handle;
            else delete handles.current[key];
          }
        ])
      ) as Record<string, (handle: Nullable<IPlaceholderFieldHandle>) => void>,
    [keys]
  );

  // The stored problems of the version the editor holds, while it holds it unchanged.
  const held = (variant?.versions ?? []).find((version) => sameBody(parts, bodyOf(parts, version), body)) ?? null;
  const stored = held ? sortProblems(held.problems ?? [], keys).byField : {};
  const local = settingProblems(body.settings);
  const problems: FieldProblems = { ...stored, ...preview.problems, ...local, ...saveProblems };
  const hasProblems = hasPreviewProblems(preview) || Object.keys(local).length > 0;
  const idle = busy === null && suggesting === null;
  const canSave = changed && idle && Object.keys(local).length === 0;
  const canPublish = (changed || draft !== null) && idle && !hasProblems;
  // A staff template's version 0 is an empty body, not a default text: nothing is sent until a
  // version is published, so it is "not published", never "failing".
  const unpublished = staff && (current?.version ?? 0) === 0;
  const failing = !unpublished && (current?.problems ?? []).length > 0;
  const scheduled = variant?.scheduled ?? null;
  const hints = qualityHints(template, body, preview.shown?.subject ?? null);
  const latest = Math.max(0, ...(variant?.versions ?? []).map((version) => version.version));
  const title = survey ? `${template.name} for ${surveyLabel(survey)}` : template.name;
  const textPlaceholders = React.useMemo(
    () => template.placeholders.filter((placeholder) => placeholder.kind !== 'url'),
    [template.placeholders]
  );
  // The sheet's words: the field a pick goes into, and what the groups after This email hold.
  const target = parts.find((part) => part.key === (focus ?? lastFocused.current))?.label ?? 'the body';
  const scope = template.placeholders.some((placeholder) => !isOwnPlaceholder(placeholder))
    ? survey
      ? `Then the fields of the ${surveyLabel(survey)} survey.`
      : 'Then the fields every survey has; a survey’s version offers its answers and staff fields too.'
    : template.group === 'account'
      ? 'An account email knows no participant or survey, so it has only its own.'
      : null;
  const insert = (name: string) => handles.current[lastFocused.current]?.insert(name);
  // A `{{…` a field handed to the sheet stays as typed once the sheet closes or the next pick
  // goes elsewhere.
  const release = (except?: string) =>
    Object.entries(handles.current).forEach(([key, handle]) => key !== except && handle?.release?.());
  // The body drawn in the colours and font of the design it is sent in.
  const look = React.useMemo(
    () => lookOf(designs.settingsOf(body.settings.design)),
    [designs, body.settings.design]
  );

  const forget = (key: string) =>
    setSaveProblems((was) => {
      if (!(key in was)) return was;
      const next = { ...was };
      delete next[key];
      return next;
    });

  const setPart = (key: string, part: MailPart) => {
    const next = { ...bodyRef.current, parts: { ...bodyRef.current.parts, [key]: part } };
    bodyRef.current = next;
    setBody(next);
    setEdits((n) => n + 1);
    forget(key);
    setLoaded(null);
  };

  const setSetting = (key: keyof IMailSettings, value: string) => {
    const next = { ...bodyRef.current, settings: { ...bodyRef.current.settings, [key]: value } };
    bodyRef.current = next;
    setBody(next);
    setEdits((n) => n + 1);
    forget(key);
  };

  /** Every rich part's last edit compiled, so what goes out is what the editor shows. */
  const flushed = async (): Promise<IMailBody> => {
    await Promise.all(Object.values(handles.current).map((handle) => handle?.flush?.()));
    return bodyRef.current;
  };

  const refused = (error: unknown, fallback: string): Nullable<string> => {
    if (error instanceof ApiError && error.isProblem(ProblemType.revisionConflict)) {
      setConflict({ text: conflictWords('email template'), alert: true });
      return null;
    }
    if (isInvalidBody(error)) {
      const { byField, other } = problemsOf(error, keys);
      setSaveProblems(byField);
      return other ?? (Object.keys(byField).length > 0 ? 'Some parts have problems: see the fields.' : fallback);
    }
    return refusalMessage(error, fallback);
  };

  /** The draft as the editor holds it, under the revision the last save left. */
  const putDraft = async (next: IMailBody, note: string) => {
    const kept = await mailTemplatesApi.saveDraft(code, survey, draftNow.current?.revision ?? 0, { ...next, note });
    draftNow.current = kept;
    savedNow.current = bodyOf(parts, kept);
    onAdopt((was) => withDraft(was, survey, kept));
  };

  const saveDraft = async (): Promise<DraftAnswer> => {
    setBusy('draft');
    setRefusal(null);
    setSaveProblems({});
    setConflict(null);
    try {
      await putDraft(await flushed(), draftNow.current?.note ?? '');
      setLoaded(null);
      return 'landed';
    } catch (error) {
      const message = refused(error, 'The draft could not be saved.');
      setRefusal(message);
      return message === null ? 'conflict' : 'refused';
    } finally {
      setBusy((was) => (was === 'draft' ? null : was));
    }
  };

  const publish = async (choice: IPublishChoice) => {
    setBusy('publish');
    // An autosave already out lands first — its end leaves this busy alone — and the draft below
    // quotes the revision it leaves.
    await saving.settle();
    setPublishRefusal(null);
    setSaveProblems({});
    setConflict(null);
    try {
      // What is published is the draft — saved first when the editor holds more — so publishing
      // leaves no draft behind.
      const next = await flushed();
      if (!draftNow.current || !sameBody(parts, next, savedNow.current)) await putDraft(next, choice.note);
      await mailTemplatesApi.publish(code, survey, latest, choice);
      setPublishOpen(false);
      saving.quiet();
      await onReload(
        choice.effectiveAt
          ? `Version ${latest + 1} is scheduled for ${formatEt(choice.effectiveAt)}.`
          : `Published as version ${latest + 1}. The next mail sent uses it.`
      );
    } catch (error) {
      const message = refused(error, 'The email could not be published.');
      if (message) setPublishRefusal(message);
      else setPublishOpen(false);
    } finally {
      setBusy(null);
    }
  };

  const act = async (kind: Exclude<Busy, null>, run: () => Promise<unknown>, message: string, fallback: string) => {
    setBusy(kind);
    setRefusal(null);
    try {
      await run();
      saving.quiet();
      await onReload(message);
    } catch (error) {
      setRefusal(refusalMessage(error, fallback));
    } finally {
      setBusy(null);
    }
  };

  // Withdraw and Remove override read the editor afresh: edits owed are saved first, or asked.
  const withdraw = (version: IMailVersion) => {
    if (!window.confirm(`Withdraw version ${version.version}? It will not take effect.`)) return;
    saving.leave(
      () =>
        void act(
          'withdraw',
          () => mailTemplatesApi.withdraw(code, survey, version.version),
          `Version ${version.version} withdrawn: it will not take effect.`,
          'The version could not be withdrawn.'
        )
    );
  };

  const removeOverride = () => {
    if (!survey) return;
    const label = surveyLabel(survey);
    if (!window.confirm(`${label} mails go back to the text for all surveys. Its versions stay in History.`)) return;
    saving.leave(
      () =>
        void act(
          'remove',
          () => mailTemplatesApi.removeVariant(code, survey),
          `${label} mails use the text for all surveys again.`,
          'The override could not be removed.'
        )
    );
  };

  const discardDraft = () => {
    const revision = draftNow.current?.revision;
    if (revision === undefined) return;
    void act(
      'discard',
      () => mailTemplatesApi.discardDraft(code, survey, revision),
      'Draft discarded.',
      'The draft could not be discarded.'
    );
  };

  // The editor's parts as they stand, rendered as the preview is, to the caller's own address.
  const sendTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      const sent = await mailTemplatesApi.test(code, await flushed(), renderOptions(options, survey));
      setTest({ sent: true, text: `Test sent to ${sent.to}.` });
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        const after = Number(error.problem?.retryAfter);
        const minutes = Number.isFinite(after) && after > 0 ? Math.ceil(after / 60) : null;
        setTest({
          sent: false,
          text: minutes
            ? `Too many tests — try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
            : 'Too many tests — try again later.'
        });
      } else {
        setTest({ sent: false, text: refusalMessage(error, 'The test could not be sent.') });
      }
    } finally {
      setTesting(false);
    }
  };

  const discard = () => {
    bodyRef.current = saved;
    setBody(saved);
    setEdits((n) => n + 1);
    setSaveProblems({});
    setRefusal(null);
    setLoaded(null);
  };

  const restore = (version: IMailVersion) => {
    const next = bodyOf(parts, version);
    bodyRef.current = next;
    setBody(next);
    setEdits((n) => n + 1);
    setSaveProblems({});
    setLoaded(version.version);
    setHistoryOpen(false);
  };

  const saving = useDraftSaving({
    changed,
    isChanged: () => !sameBody(parts, bodyRef.current, savedNow.current),
    pending: () => Object.values(handles.current).some((handle) => handle?.pending?.() === true),
    flushEdits: flushed,
    hasDraft: draft !== null,
    draftAt: draft?.updatedAt ?? null,
    savable: Object.keys(local).length === 0 && !hasPreviewProblems(preview) && suggesting === null,
    flushable: Object.keys(local).length === 0 && suggesting === null,
    canSave,
    canPublish,
    busy,
    edits,
    // The sender, Reply-To and design count as one: Sender and design.
    count: () => {
      const keys = changedKeys(parts, bodyRef.current, savedNow.current);
      const settings = keys.filter((key) => !parts.some((part) => part.key === key)).length;
      return keys.length - settings + (settings > 0 ? 1 : 0);
    },
    refusal,
    save: saveDraft,
    onPublish: () => {
      setPublishRefusal(null);
      setPublishOpen(true);
    },
    onDiscardChanges: discard,
    onDiscardDraft: discardDraft,
    discardDraftWords: 'The draft is deleted and the editor goes back to the live version, with every change since.'
  });

  /** *Reload* after a conflict: the template as saved now, the editor's changes kept, unsaved. */
  const reloadKept = async () => {
    await onRefetch();
    saving.resume();
    setConflict({ text: 'The email template as saved now is read — your changes are still here, unsaved.', alert: false });
  };

  const pickedVersion = (variant?.versions ?? []).find((version) => version.version === picked) ?? null;
  const switchTo = (next: Nullable<MailSurvey>) => {
    if (next !== survey) saving.leave(() => onSurvey(next));
  };

  return (
    <EditorLookProvider value={look}>
      <div className={cn('grid min-w-0 gap-4', (historyOpen || deliveryOpen) && HISTORY_ROOM)} data-mail-template={code}>
        <div className="grid gap-2 rounded-md border border-border bg-white px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex min-w-0 items-center gap-1">
              <TemplatePicker
                value={code}
                templates={templates}
                unsaved={saving.unsaved}
                onSelect={(next) => {
                  if (next !== code) saving.leave(() => onSelect(next));
                }}
              />
              <TemplateHelp template={template} />
            </div>
            <UsageChips usages={template.usages} />
            {template.archivedAt && <StatusChip tone="attention">Archived</StatusChip>}
            <span
              className="text-xs text-muted-foreground"
              title={current?.createdBy?.email ?? undefined}
              data-current-version=""
            >
              {current && !unpublished
                ? versionStamp(current.version, current.effectiveAt ?? current.createdAt, null)
                : 'Not published'}
            </span>
            {failing && <StatusChip tone="attention">Not in use</StatusChip>}
            {loaded !== null ? (
              <StatusChip tone="info">
                <span role="status">{loaded === 0 ? 'Default' : `Version ${loaded}`} restored, not saved</span>
              </StatusChip>
            ) : null}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-expanded={historyOpen}
                onClick={() => {
                  setPicked(current?.version ?? variant?.versions[0]?.version ?? null);
                  setDeliveryOpen(false);
                  setHistoryOpen((open) => !open);
                }}
              >
                <History className="size-4" aria-hidden />
                History
              </Button>
              {canLog && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-expanded={deliveryOpen}
                  onClick={() => {
                    setHistoryOpen(false);
                    setDeliveryOpen((open) => !open);
                  }}
                  data-open-delivery=""
                >
                  <BarChart3 className="size-4" aria-hidden />
                  Delivery
                </Button>
              )}
              {saving.controls}
              {menu?.(saving.leave)}
            </div>
          </div>
          <HeaderTags tags={template.tags} editable onSave={onTags} />
          {template.surveyScoped && (
            <VariantSwitcher
              template={template}
              survey={survey}
              own={own}
              busy={busy !== null}
              onSwitch={switchTo}
              onRemove={removeOverride}
            />
          )}
        </div>

        {said && (
          <p className="text-sm" role="status" data-mail-said="">
            {said}
          </p>
        )}
        {failing && current && (
          <Alert variant="destructive" data-current-failing="">
            <AlertTitle>Version {current.version} no longer passes the checks</AlertTitle>
            <AlertDescription>
              Mails go out with the default text. Fix it and publish.
              {sortProblems(current.problems, keys).other && (
                <span className="block">{sortProblems(current.problems, keys).other}</span>
              )}
            </AlertDescription>
          </Alert>
        )}
        {scheduled && (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-[color-mix(in_srgb,var(--color-teal)_35%,white)] bg-[color-mix(in_srgb,var(--color-turquoise)_8%,white)] px-4 py-2 text-sm"
            data-mail-scheduled={scheduled.version}
          >
            <span>
              <span className="font-medium">Version {scheduled.version}</span> is scheduled for{' '}
              {formatEt(scheduled.effectiveAt ?? '')}.
              {scheduled.note && <span className="text-muted-foreground"> {scheduled.note}</span>}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              disabled={busy !== null}
              onClick={() => withdraw(scheduled)}
            >
              Withdraw
            </Button>
          </div>
        )}
        {conflict && (
          <div
            className={cn('flex flex-wrap items-center gap-2 text-sm', conflict.alert ? 'text-destructive' : 'text-muted-foreground')}
            role={conflict.alert ? 'alert' : 'status'}
            data-mail-conflict=""
          >
            <span>{conflict.text}</span>
            {conflict.alert && (
              <Button type="button" size="sm" variant="outline" onClick={() => void reloadKept()}>
                Reload
              </Button>
            )}
          </div>
        )}
        {refusal && (
          <p className="text-sm text-destructive" role="alert">
            {refusal}
          </p>
        )}

        {historyOpen && pickedVersion && (
          <VersionView
            key={pickedVersion.version}
            template={template}
            survey={survey}
            version={pickedVersion}
            against={current}
            options={options}
            onOptions={onOptions}
          />
        )}

        {/* Hidden, not unmounted, while History is open: the parts keep their undo history. */}
        <div className={cn('grid min-w-0 items-start gap-6 lg:grid-cols-2', historyOpen && pickedVersion && 'hidden')}>
          <section
            aria-labelledby="mail-template-content"
            className="grid min-w-0 gap-4 rounded-md border border-border bg-white p-4 text-[0.8125rem]"
          >
            <div className="flex items-center justify-between gap-3">
              <h2 id="mail-template-content" className="text-base font-semibold text-[var(--color-royal-blue)]">
                Content
              </h2>
              <CommandsButton />
              <PlaceholderSheet
                ref={sheet}
                sections={sections}
                target={target}
                scope={scope}
                onInsert={insert}
                onClosed={() => release()}
                onReturn={() => handles.current[lastFocused.current]?.focus()}
              />
            </div>
            {parts.map((definition) => {
              const { key, label } = definition;
              const id = `mail-template-${key}`;
              const labelId = `${id}-label`;
              const problemId = `${id}-problem`;
              const hintId = `${id}-hint`;
              const describedBy = [problems[key] && problemId, hints[key] && hintId].filter(Boolean).join(' ');
              const common = {
                id,
                // A URL placeholder is an address, never text: a line part does not offer it.
                placeholders: definition.kind === 'rich' ? template.placeholders : textPlaceholders,
                sections,
                onBrowse: (query: string) => {
                  lastFocused.current = key;
                  sheet.current?.browse(query);
                },
                labelledBy: labelId,
                describedBy: describedBy || undefined,
                invalid: Boolean(problems[key]),
                onFocus: () => {
                  lastFocused.current = key;
                  setFocus(key);
                  release(key);
                },
                onBlur: () => setFocus((was) => (was === key ? null : was)),
                onSuggesting: (open: boolean) => setSuggesting((was) => (open ? key : was === key ? null : was)),
                handle: register[key]
              };
              const part = body.parts[key];
              return (
                <div key={key} className="grid gap-1" data-mail-field={key}>
                  <span className="flex items-center gap-1">
                    <span
                      id={labelId}
                      className="w-fit cursor-default font-medium"
                      onClick={() => handles.current[key]?.focus('end')}
                    >
                      {label}
                    </span>
                    {definition.help && (
                      <HelpPopover label={`About ${label}`} className="size-6">
                        <p>{definition.help}</p>
                      </HelpPopover>
                    )}
                  </span>
                  {definition.kind === 'rich' && isRich(part) ? (
                    <RichField
                      {...common}
                      value={part}
                      conditions={template.conditions}
                      onChange={(value) => setPart(key, value)}
                      whole
                      lists={template.lists}
                      systemBlocks={template.systemBlocks}
                    />
                  ) : (
                    <PlaceholderField
                      {...common}
                      value={lineText(part)}
                      multiline={false}
                      onChange={(value) => setPart(key, { text: value })}
                    />
                  )}
                  {problems[key] && (
                    <p id={problemId} className="text-xs text-destructive">
                      {problems[key]}
                    </p>
                  )}
                  {hints[key] && (
                    <p id={hintId} className="text-xs text-muted-foreground" data-mail-hint={key}>
                      {hints[key]}
                    </p>
                  )}
                </div>
              );
            })}
            <SenderSection
              open={senderOpen}
              onOpenChange={setSenderOpen}
              designs={designs.choices}
              settings={body.settings}
              problems={problems}
              onChange={setSetting}
            />
          </section>

          <section
            aria-labelledby="mail-template-preview"
            className="flex min-w-0 flex-col gap-3 rounded-md border border-border bg-white p-4 lg:sticky lg:top-[calc(var(--compact-bar-height,48px)+1rem)] lg:h-[calc(100vh-var(--compact-bar-height,48px)-2rem)]"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 id="mail-template-preview" className="text-base font-semibold text-[var(--color-royal-blue)]">
                Preview
              </h2>
              {test && (
                <span
                  role="status"
                  className={cn('text-xs', test.sent ? 'text-muted-foreground' : 'text-destructive')}
                  data-mail-test=""
                >
                  {test.text}
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={hasProblems || suggesting !== null || testing}
                onClick={() => void sendTest()}
              >
                <Send className="size-4" aria-hidden />
                {testing ? 'Sending…' : 'Send me a test'}
              </Button>
            </div>
            <PreviewToolbar template={template} options={options} onChange={onOptions} />
            <PreviewBody
              state={preview}
              options={options}
              focus={focus}
              onPick={(key) =>
                key.startsWith(BLOCK_PICK)
                  ? handles.current.body?.selectBlock?.(key.slice(BLOCK_PICK.length))
                  : handles.current[key]?.focus('end')
              }
              sender={body.settings.fromName}
              fill
            />
          </section>
        </div>

        <HistorySheet
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          template={template}
          survey={survey}
          variant={variant}
          picked={pickedVersion?.version ?? null}
          onPick={setPicked}
          onRestore={restore}
        />
        {canLog && <DeliverySheet open={deliveryOpen} onOpenChange={setDeliveryOpen} template={template} />}
        <PublishDialog
          open={publishOpen}
          onOpenChange={setPublishOpen}
          title={title}
          scheduled={scheduled}
          initialNote={draft?.note ?? ''}
          busy={busy === 'publish'}
          refusal={publishRefusal}
          onPublish={(choice) => void publish(choice)}
        />
        {saving.dialogs}
      </div>
    </EditorLookProvider>
  );
}

function blockNames(template: IMailTemplate): string {
  return template.systemBlocks.map((block) => block.label).join(', ');
}

/** What the template is and how its editor works — one press away. */
function TemplateHelp({ template }: { template: IMailTemplate }) {
  return (
    <HelpPopover label={`About ${template.name}`}>
      <p>{template.purpose}</p>
      {template.surveyScoped && (
        <p>
          The text for all surveys is sent unless a survey has text of its own: pick the survey
          above the fields to give it its own, and Remove override to send it the text for all
          surveys again.
        </p>
      )}
      <p>
        The subject and the preview text are lines with placeholders; the body is the mail itself:
        headings, paragraphs, lists, buttons, images and more. A block shown only if a condition
        holds — to the CC address, with the PDF — is switched by the preview&apos;s Conditions; a
        placeholder&apos;s fallback is the words sent when its value is empty. Commands and
        shortcuts: the keyboard button by Content, or Ctrl+/.
      </p>
      {template.systemBlocks.length > 0 && (
        <p>
          The striped cards are the portal&apos;s own blocks: {blockNames(template)}. Their content
          comes from the portal; move them where they belong. A required one stays in the body;{' '}
          <code>/</code> → Portal blocks puts back one that is missing.
        </p>
      )}
      <p>
        Your work is kept as a draft as you type (Save ▾ → Autosave drafts; off, Save draft): nothing
        is sent with it. Publish… makes it the next version, now or from a date
        and time (ET); a scheduled version can be withdrawn before it takes effect. History lists
        every version; restoring one puts it in the editor.
      </p>
    </HelpPopover>
  );
}

/** Which survey's text is edited, and whether it has its own. */
function VariantSwitcher({
  template,
  survey,
  own,
  busy,
  onSwitch,
  onRemove
}: {
  template: IMailTemplate;
  survey: Nullable<MailSurvey>;
  own: boolean;
  busy: boolean;
  onSwitch: (survey: Nullable<MailSurvey>) => void;
  onRemove: () => void;
}) {
  const choices: { value: Nullable<MailSurvey>; label: string; own: boolean }[] = [
    { value: null, label: 'All surveys', own: false },
    ...SURVEYS.map((entry) => ({
      value: entry.value,
      label: entry.label,
      own: ownText(template.variants.find((variant) => variant.survey === entry.value) ?? null)
    }))
  ];
  const buttons = React.useRef<(HTMLButtonElement | null)[]>([]);
  const index = choices.findIndex((choice) => choice.value === survey);
  const move = (event: React.KeyboardEvent, at: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (at + step + choices.length) % choices.length;
    buttons.current[next]?.focus();
    onSwitch(choices[next].value);
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border pt-2 text-[0.8125rem]">
      <span id="mail-template-variant" className="text-xs text-muted-foreground">
        Text for
      </span>
      <div
        role="radiogroup"
        aria-labelledby="mail-template-variant"
        className="inline-flex h-8 items-stretch rounded-md border border-border bg-[var(--color-slight-blue,#f3f6fa)] p-0.5"
      >
        {choices.map((choice, at) => {
          const checked = at === index;
          return (
            <button
              key={choice.label}
              ref={(element) => {
                buttons.current[at] = element;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              onClick={() => onSwitch(choice.value)}
              onKeyDown={(event) => move(event, at)}
              data-variant={choice.value ?? 'base'}
              className={cn(
                'inline-flex items-center gap-1.5 rounded px-2.5 text-xs font-medium whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring',
                checked
                  ? 'bg-white text-[var(--color-royal-blue)] shadow-sm'
                  : 'text-muted-foreground hover:text-[var(--color-royal-blue)]'
              )}
            >
              {choice.label}
              {choice.own && (
                <>
                  <span aria-hidden className="size-1.5 rounded-full bg-[var(--color-teal)]" />
                  <span className="sr-only">, own text</span>
                </>
              )}
            </button>
          );
        })}
      </div>
      {survey && (
        <>
          <StatusChip tone={own ? 'success' : 'neutral'}>{own ? 'Own text' : 'Uses the base'}</StatusChip>
          {own && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onRemove}>
              Remove override
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** The sender's name, Reply-To and the design — collapsed, their state said on the closed line. */
function SenderSection({
  open,
  onOpenChange,
  designs,
  settings,
  problems,
  onChange
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  designs: readonly { value: string; label: string }[];
  settings: IMailSettings;
  problems: FieldProblems;
  onChange: (key: keyof IMailSettings, value: string) => void;
}) {
  const failing = Boolean(problems.fromName || problems.replyTo);
  const designLabel = designs.find((design) => design.value === settings.design)?.label ?? settings.design;
  const summary = [
    settings.fromName.trim() || 'Veritant',
    settings.replyTo.trim() ? `replies to ${settings.replyTo.trim()}` : 'replies to the From address',
    `${designLabel} design`
  ].join(' · ');
  const field = (key: 'fromName' | 'replyTo', label: string, placeholder: string, max: number) => (
    <div className="grid gap-1">
      <label htmlFor={`mail-template-${key}`} className="font-medium">
        {label}
      </label>
      <Input
        id={`mail-template-${key}`}
        value={settings[key]}
        maxLength={max}
        placeholder={placeholder}
        aria-invalid={problems[key] ? true : undefined}
        aria-describedby={problems[key] ? `mail-template-${key}-problem` : undefined}
        onChange={(event) => onChange(key, event.target.value)}
        className="h-9"
      />
      {problems[key] && (
        <p id={`mail-template-${key}-problem`} className="text-xs text-destructive">
          {problems[key]}
        </p>
      )}
    </div>
  );
  return (
    <Collapsible open={open || failing} onOpenChange={onOpenChange} className="border-t border-border pt-3">
      <div className="flex items-center gap-1">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 items-center gap-1.5 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-mail-sender=""
          >
            <ChevronRight className={cn('size-4 shrink-0 transition-transform', (open || failing) && 'rotate-90')} aria-hidden />
            <span className="font-medium">Sender and design</span>
            {!(open || failing) && <span className="truncate text-muted-foreground">{summary}</span>}
          </button>
        </CollapsibleTrigger>
        <HelpPopover label="About the sender and design" className="size-6">
          <p>
            The name an inbox shows for this email, and where a reply goes. Empty, the name is
            the portal&apos;s and replies go to the From address. The From address itself stays the
            portal&apos;s, so the mail keeps passing the domain checks.
          </p>
          <p>
            The design is the frame the mail is drawn in. <em>Veritant</em>: the band, the card,
            the colours and the font of Email templates → Design. <em>Plain</em>: the body alone,
            like a personal note.
          </p>
        </HelpPopover>
      </div>
      <CollapsibleContent className="mt-3 grid gap-3 sm:grid-cols-2">
        {field('fromName', 'Sender name', 'Veritant', 80)}
        {field('replyTo', 'Reply-To', 'The From address', 254)}
        <div className="grid gap-1" data-mail-design="">
          <span id="mail-template-design" className="font-medium">
            Design
          </span>
          <ChoiceSelect
            labelledBy="mail-template-design"
            value={settings.design}
            onChange={(value) => onChange('design', value)}
            choices={
              designs.some((design) => design.value === settings.design)
                ? designs
                : [...designs, { value: settings.design, label: settings.design }]
            }
            className="h-9"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
